import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  AlertTriangle, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  Settings,
  ClipboardList,
  ScrollText,
  FileDown,
  Inbox,
  Receipt,
  Euro,
  Gauge,
  TrendingUp,
  BarChart3,
  Search as SearchIcon,
  UserPlus,
  Archive,
  HandCoins,
  DatabaseBackup,
  HelpCircle,
  Mail
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { NotificationBell } from '../NotificationBell';

const navItems = [
  { href: '/admin/cockpit', icon: Gauge, label: 'Cockpit' },
  { href: '/admin/executive', icon: TrendingUp, label: 'Direction' },
  { href: '/admin/analytics', icon: BarChart3, label: 'Pilotage' },
  { href: '/admin/search', icon: SearchIcon, label: 'Recherche' },
  { href: '/admin', icon: LayoutDashboard, label: 'Tableau de bord' },
  { href: '/admin/orders', icon: Inbox, label: 'Commandes' },
  { href: '/admin/email-inbox', icon: Mail, label: 'Boîte email' },
  { href: '/admin/invoicing', icon: Receipt, label: 'Facturation' },
  { href: '/admin/pricing', icon: Euro, label: 'Grilles tarifaires' },
  { href: '/admin/partners', icon: HandCoins, label: 'Associés' },
  { href: '/admin/labs', icon: Building2, label: 'Laboratoires' },
  { href: '/admin/clients', icon: Users, label: 'Clients' },
  { href: '/admin/registrations', icon: UserPlus, label: 'Inscriptions' },
  { href: '/admin/alerts', icon: AlertTriangle, label: 'Alertes' },
  { href: '/admin/reports', icon: ClipboardList, label: 'Reportings QCM' },
  { href: '/admin/audit', icon: ScrollText, label: "Journal d'audit" },
  { href: '/admin/exports', icon: FileDown, label: 'Exports CSV' },
  { href: '/admin/retention', icon: Archive, label: 'Rétention' },
  { href: '/admin/backups', icon: DatabaseBackup, label: 'Sauvegardes' },
  { href: '/admin/settings', icon: Settings, label: 'Paramètres' },
  { href: '/aide', icon: HelpCircle, label: 'Aide' },
];

export const AdminLayout = ({ children, title, subtitle }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="admin-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-50 h-full w-64 bg-background border-r border-border transition-transform duration-200 lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-border">
            <Link to="/admin" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_dental-chat-5/artifacts/vbzuvp2d_1000265186.jpg" 
                alt="JPD Logo" 
                className="w-10 h-10 object-contain"
              />
              <span className="font-bold text-lg tracking-tight">Jeff P. Dental</span>
            </Link>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    isActive 
                      ? "bg-primary text-primary-foreground" 
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                  {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">
                  {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground">Administrateur</p>
              </div>
            </div>
            <NotificationBell withLabel />
            <Button 
              variant="outline" 
              className="w-full justify-start gap-2"
              onClick={logout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
              Déconnexion
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-background/95 backdrop-blur border-b border-border">
          <div className="flex items-center justify-between h-full px-6">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-button"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold tracking-tight">{title}</h1>
                {subtitle && (
                  <p className="text-sm text-muted-foreground">{subtitle}</p>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
